import React, { useState } from "react";
import { toast } from "react-toastify";
import { deletePlannedPrice } from "../api_controll/apiAdmin";

export const transformPlannedPricesData = (apiResponse) => {
  const items = Array.isArray(apiResponse) ? apiResponse : apiResponse?.items ?? [];
  if (!Array.isArray(items)) return [];

  const byValidFrom = new Map();

  for (const p of items) {
    const key = p.valid_from ?? "__no_valid_from__";

    if (!byValidFrom.has(key)) {
      byValidFrom.set(key, {
        valid_from: p.valid_from ?? null,
        updated_by: p.updated_by ?? null,
        station_id: p.station_id ?? null,
        total_prices: 0,
        prices: {},
      });
    }

    const bucket = byValidFrom.get(key);
    const fuel = p.price_type;
    const currency = p.currency;

    if (!bucket.prices[fuel]) bucket.prices[fuel] = {};

    bucket.prices[fuel][currency] = {
      value: p.value,
      id: p.id,
      created_at: p.created_at,
    };

    bucket.total_prices += 1;
  }

  return [...byValidFrom.values()];
};

const parseDateSafe = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatValidFrom = (v) => {
  const d = parseDateSafe(v);
  if (!d) return "---";

  return d.toLocaleString("cs-CZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const removePlannedIdFromSets = (sets, id) => {
  return (sets || [])
    .map((priceSet) => {
      const updatedPrices = { ...(priceSet?.prices || {}) };

      Object.keys(updatedPrices).forEach((fuelType) => {
        const currencies = { ...(updatedPrices[fuelType] || {}) };

        Object.keys(currencies).forEach((currency) => {
          if (currencies[currency]?.id === id) {
            delete currencies[currency];
          }
        });

        if (Object.keys(currencies).length === 0) {
          delete updatedPrices[fuelType];
        } else {
          updatedPrices[fuelType] = currencies;
        }
      });

      return {
        ...priceSet,
        prices: updatedPrices,
        total_prices: Math.max(0, Number(priceSet?.total_prices || 0) - 1),
      };
    })
    .filter((priceSet) => Object.keys(priceSet?.prices || {}).length > 0);
};

const toFuelClass = (fuelType) =>
  `fuel-${String(fuelType).toLowerCase().replace(/\s+/g, "-")}`;

export default function PlannedPricesSection({
  plannedPrices,
  setPlannedPrices,
  showPlannedPrices,
  setShowPlannedPrices,
  canPlannedDelete,
  fuelOrderFromBackend,
}) {
  const [openLocal, setOpenLocal] = useState(false);

  if (!plannedPrices || plannedPrices.length === 0) return null;

  const open = typeof showPlannedPrices === "boolean" ? showPlannedPrices : openLocal;

  const toggleOpen = () => {
    if (typeof setShowPlannedPrices === "function") setShowPlannedPrices(!open);
    else setOpenLocal((s) => !s);
  };

  const handleRemovePlannedPrice = async (id) => {
    try {
      await deletePlannedPrice(id);

      if (typeof setPlannedPrices === "function") {
        setPlannedPrices((prev) => removePlannedIdFromSets(prev, id));
      }

      toast.success("Plánovaná cena uspěšně zrušena.");
    } catch (err) {
      console.error(err);
      toast.error("Nepodařilo se zrušit plánovanou cenu.");
    }
  };

  const resolveFuelOrder = (priceSet) => {
    const backend = Array.isArray(fuelOrderFromBackend) ? fuelOrderFromBackend : null;
    const present = Object.keys(priceSet?.prices || {});

    if (!backend) return present;

    const ordered = backend.filter((f) => present.includes(f));

    for (const f of present) {
      if (!ordered.includes(f)) ordered.push(f);
    }

    return ordered;
  };

  const resolveCurrencyOrder = (currenciesObj) => {
    const keys = Object.keys(currenciesObj || {});
    const out = [];

    if (keys.includes("CZK")) out.push("CZK");
    if (keys.includes("EUR")) out.push("EUR");

    for (const k of keys) {
      if (k !== "CZK" && k !== "EUR") out.push(k);
    }

    return out;
  };

  return (
    <section className="plannedModern">
      <button
        type="button"
        className="plannedModern__toggle"
        onClick={toggleOpen}
        aria-expanded={open}
      >
        <span className="plannedModern__toggleText">
          <strong>Plánované ceny</strong>
          <small>{plannedPrices.length} plánovaných sad</small>
        </span>

        <span className="plannedModern__toggleMeta">
          {/* <span className="plannedModern__count">{plannedPrices.length}</span> */}
          <span className={`plannedModern__chevron ${open ? "is-open" : ""}`}>
            ▾
          </span>
        </span>
      </button>

      {open && (
        <div className="plannedModern__content">
          {plannedPrices.map((priceSet, index) => (
            <article key={index} className="plannedModern__set">
              <header className="plannedModern__setHeader">
                <div>
                  <span className="plannedModern__label">Platné od</span>
                  <strong>{formatValidFrom(priceSet.valid_from)}</strong>
                </div>

                <span className="plannedModern__badge">
                  {priceSet.total_prices} cen
                </span>
              </header>

              <div className="plannedModern__grid">
                {resolveFuelOrder(priceSet).map((fuelType) => {
                  const currencies = priceSet?.prices?.[fuelType] || {};
                  const currencyOrder = resolveCurrencyOrder(currencies);

                  return (
                    <div
                      key={fuelType}
                      className={`plannedModern__fuel ${toFuelClass(fuelType)}`}
                    >
                      <div className="plannedModern__fuelTitle">{fuelType}</div>

                      <div className="plannedModern__currencyList">
                        {currencyOrder.map((currency) => {
                          const data = currencies?.[currency];
                          if (!data) return null;

                          return (
                            <div key={currency} className="plannedModern__priceRow">
                              <span className="plannedModern__currency">
                                {currency}
                              </span>

                              <strong className="plannedModern__value">
                                {data.value}
                              </strong>

                              {canPlannedDelete && (
                                <button
                                  type="button"
                                  className="plannedModern__remove"
                                  onClick={() => handleRemovePlannedPrice(data.id)}
                                  title="Odebrat cenu"
                                  aria-label={`Odebrat plánovanou cenu ${fuelType} ${currency}`}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}